import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Card } from "reactstrap";
import {
  Button,
  ConfigProvider,
  Empty,
  Skeleton,
  Space,
  Spin,
  Tabs,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import Head from "../../../../layout/head/Head";
import Content from "../../../../layout/content/Content";
import { updateMenuData } from "../../../../store/slices/misc/menuData";
import TopMenu from "../../../../route/topMenu";
import { threeMReportApis } from "../../../../api/ThreeMReport";
import {
  MEASURE_FIELDS,
  OutflowMeasure,
  TargetExecutedAnalysis,
  ThreeMOutflowAnalysis,
} from "./ExecutionChartAnalysis";
import {
  ExecutionSummaryData,
  fmtMonth,
  fmtMonthLong,
} from "./executionShared";

// Detailed analysis for the two Monthly Execution charts — a SEPARATE SCREEN,
// like MonthDetailPage, rather than a modal: the analysis carries a month
// table, an insights rail and a full month drill-down, which is more than a
// dialog should hold, and putting it on a route makes every state shareable.
//
// Everything that decides what you're looking at lives in the QUERY STRING —
// view (which chart), month (drilled or not), measure (3M pillar), plus the
// window/filters the report was showing — so the page reproduces the exact
// figures behind the card that was clicked, survives a refresh, and the browser
// back button steps back out of a drill. Identity (projectName, fullscreen)
// rides in location.state, matching MonthDetailPage.

type AnalysisView = "target-executed" | "outflow";

const VIEWS: Array<{ key: AnalysisView; label: string }> = [
  { key: "target-executed", label: "Planned vs Executed" },
  { key: "outflow", label: "3M Expenditure" },
];

const isMeasure = (value: string | null): value is OutflowMeasure =>
  !!value && value in MEASURE_FIELDS;

const MonthlyExecutionAnalysisPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const search = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const state = (location.state as any) || {};
  const projectId = state.id ?? params.projectId;
  const projectNameFromState = state.projectName;

  const view: AnalysisView =
    search.get("view") === "outflow" ? "outflow" : "target-executed";
  const drillMonth = search.get("month");
  const measure: OutflowMeasure = isMeasure(search.get("measure"))
    ? (search.get("measure") as OutflowMeasure)
    : "total";
  const fromMonth = search.get("fromMonth") || undefined;
  const toMonth = search.get("toMonth") || undefined;
  const phaseId = search.get("phaseId") ? Number(search.get("phaseId")) : undefined;
  const activityId = search.get("activityId")
    ? Number(search.get("activityId"))
    : undefined;

  const [data, setData] = useState<ExecutionSummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  useEffect(() => {
    if (state.fullscreen && !document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.().catch(() => {
        /* no gesture left — degrade to normal view */
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      containerRef.current?.requestFullscreen?.().catch(() => {
        message.warning("Fullscreen is not supported in this browser");
      });
    }
  };

  const popupContainer = () => containerRef.current || document.body;

  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => ({ ...item, linkState: location.state }))
      )
    );
  }, [dispatch, location]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Same call, same arguments as the report page — the analysis must never show
  // a different number than the card it was opened from.
  useEffect(() => {
    if (!projectId) {
      setError("Project ID not found");
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    threeMReportApis
      .getMonthlyExecutionSummary(projectId, { fromMonth, toMonth, phaseId, activityId })
      .then((response) => {
        if (cancelled) return;
        if (response?.status) setData(response?.data || {});
        else {
          setError(response?.message || "Failed to load the Monthly Execution Summary");
          message.error(response?.message || "Failed to load the Monthly Execution Summary");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError("Error loading Monthly Execution Summary");
          message.error("Error loading Monthly Execution Summary");
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [projectId, fromMonth, toMonth, phaseId, activityId, refreshTick]);

  const projectName = data?.projectName || projectNameFromState || "Project";
  const months = useMemo(() => data?.months || [], [data]);

  // One writer for the whole view state. `replace` on drill changes would break
  // "back steps out of the drill", so every change pushes.
  const setQuery = (next: Record<string, string | null>) => {
    const params = new URLSearchParams(location.search);
    Object.entries(next).forEach(([key, value]) => {
      if (value === null || value === "") params.delete(key);
      else params.set(key, value);
    });
    navigate(`${location.pathname}?${params.toString()}`, { state: location.state });
  };

  const goBack = () =>
    navigate(`/pms/project/monthly-execution/${projectId}`, {
      state: { ...state, fullscreen: isFullscreen },
    });

  const openMonthDetail = (month: string, tab: string) =>
    navigate(
      `/pms/project/monthly-execution-month/${projectId}?month=${month}&tab=${tab}`,
      {
        state: {
          ...state,
          id: projectId,
          projectName,
          phaseId,
          activityId,
          fullscreen: isFullscreen,
        },
      }
    );

  const panelProps = {
    months,
    projectId,
    phaseId,
    activityId,
    drillMonth,
    onDrillMonthChange: (month: string | null) => setQuery({ month }),
    onOpenMonthDetail: openMonthDetail,
  };

  const windowLabel = useMemo(() => {
    if (fromMonth && toMonth) return `${fmtMonth(fromMonth)} – ${fmtMonth(toMonth)}`;
    return "Entire project";
  }, [fromMonth, toMonth]);

  if (!projectId) {
    return (
      <Content>
        <Empty description="Missing project context" style={{ padding: "80px 0" }} />
      </Content>
    );
  }

  return (
    <>
      <Head title="Monthly Execution — Analysis" />
      <Content>
        <div
          ref={containerRef}
          style={{
            position: "relative",
            ...(isFullscreen
              ? { background: "#f5f6fa", overflowY: "auto", padding: 16 }
              : {}),
          }}
        >
          <ConfigProvider getPopupContainer={popupContainer}>
            <Card className="card-bordered" style={{ minHeight: "85vh" }}>
              <div className="card-inner card-inner-lg">
                {/* Header */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    flexWrap: "nowrap",
                    gap: 12,
                    marginBottom: 16,
                  }}
                >
                  <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                    <Button
                      type="link"
                      icon={<ArrowLeftOutlined />}
                      style={{ paddingLeft: 0 }}
                      onClick={goBack}
                    >
                      Monthly Execution
                    </Button>
                    <h5 style={{ fontWeight: 600, marginBottom: 2 }}>
                      Execution Analysis
                      {drillMonth ? ` — ${fmtMonthLong(drillMonth)}` : ""}
                    </h5>
                    <span style={{ color: "#8c8c8c", fontSize: 13 }}>
                      {projectName} · {windowLabel}
                      {phaseId ? " · phase filtered" : ""}
                      {activityId ? " · activity filtered" : ""}
                    </span>
                  </div>
                  <Space
                    wrap
                    style={{ marginLeft: "auto", flexShrink: 0, justifyContent: "flex-end" }}
                  >
                    <Button
                      icon={
                        isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />
                      }
                      onClick={toggleFullscreen}
                    >
                      {isFullscreen ? "Exit Full Screen" : "Full Screen"}
                    </Button>
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={() => setRefreshTick((t) => t + 1)}
                      disabled={loading}
                    >
                      Refresh
                    </Button>
                  </Space>
                </div>

                {loading && !data ? (
                  <Skeleton active paragraph={{ rows: 10 }} />
                ) : error && !data ? (
                  <Empty description={error} style={{ padding: "60px 0" }} />
                ) : (
                  <Spin spinning={loading}>
                    <Tabs
                      activeKey={view}
                      // Switching charts drops the drill: a month drilled on one
                      // chart says nothing about the other's breakdown.
                      onChange={(key) => setQuery({ view: key, month: null })}
                      items={VIEWS.map((entry) => ({
                        key: entry.key,
                        label: entry.label,
                        children:
                          entry.key === "target-executed" ? (
                            <TargetExecutedAnalysis {...panelProps} />
                          ) : (
                            <ThreeMOutflowAnalysis
                              {...panelProps}
                              measure={measure}
                              onMeasureChange={(next) => setQuery({ measure: next })}
                            />
                          ),
                      }))}
                    />
                  </Spin>
                )}
              </div>
            </Card>
          </ConfigProvider>
        </div>
      </Content>
    </>
  );
};

export default MonthlyExecutionAnalysisPage;
